import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { colors } from "@ui/style/colors";
import React, { CSSProperties, ReactNode, useMemo } from "react";

export interface ConditionalSkeletonProps {
	active: boolean;
	loading?: boolean;
	className?: string;
	skeletonStyle?: CSSProperties;
	children?: ReactNode;
	contentDriven?: boolean;
}

export const ConditionalSkeleton: React.FC<ConditionalSkeletonProps> = ({
	active,
	loading = false,
	className,
	skeletonStyle,
	children,
	contentDriven = false,
}) => {
	const skeleton = useMemo(
		() => (
			<SkeletonWrapper className={className}>
				{children}
				<Skeleton style={skeletonStyle} isLoading={loading} />
			</SkeletonWrapper>
		),
		[children, className, loading, skeletonStyle],
	);

	let content: React.ReactNode;
	if (active) {
		if (contentDriven) {
			content = <div style={{ width: "fit-content" }}>{skeleton}</div>;
		} else {
			content = skeleton;
		}
	} else {
		content = <>{children}</>;
	}

	return content;
};

const SkeletonWrapper = styled.div`
	display: flex;
	position: relative;
	padding: 0 2px;
`;

const loadingAnimation = keyframes`
    0% {
        background-position: 0% 0%
    }
    100% {
        background-position: 89% 89%
    }
`;

const Skeleton = styled.div<{ isLoading: boolean }>`
	position: absolute;
	inset: 0;
	background: ${colors.grey10};
	border-radius: ${({ style }) => {
		let radius = "20px";
		if (style?.borderRadius) {
			if (typeof style.borderRadius === "number") {
				radius = `${style.borderRadius}px`;
			} else {
				radius = style.borderRadius as string;
			}
		}
		return radius;
	}};
	z-index: 20;

	${({ isLoading, style }) => {
		const backgroundColor = style?.backgroundColor ?? colors.grey10;
		return (
			isLoading &&
			css`
				background: linear-gradient(313deg, ${backgroundColor}, ${colors.white}, ${backgroundColor}, ${colors.white});
				background-position: 100% 100%;
				background-size: 400% 400%;
				animation: ${loadingAnimation} ease-in-out infinite 1s;
			`
		);
	}}
`;
